import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { MapContainer, TileLayer, Polyline, CircleMarker, useMapEvents } from "react-leaflet";
import api, { formatApiError, fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, OrderStatusBadge } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import OrderDetailDialog from "@/components/OrderDetailDialog";
import { buildOrderPdfBase64 } from "@/lib/orderPdf";
import { computeShipping, vatTreatment } from "@/lib/shipping";
import { computeVolumeDiscount, effectivePct, parseDiscountExpr } from "@/lib/sales";
import { AddressFields, addrLine, emptyAddr } from "@/components/AddressFields";
import ResellerNotifications from "@/components/ResellerNotifications";
import ProductDetailDialog from "@/components/ProductDetailDialog";
import { ContactCompanyDialog } from "@/components/ContactCompanyDialog";
import { useI18n } from "@/i18n";
import { LangSwitcher } from "@/components/LangSwitcher";
import { toast } from "sonner";
import {
  ShoppingCart, Trash2, Plus, Minus, Package, Download, Sparkles, Loader2,
  Images, Map as MapIcon, MapPin, Copy, Check, Mountain, Route as RouteIcon,
  FileText, Film, FileArchive, Image as ImageIcon,
  Eye, CalendarDays, Truck, User, Building2, Pencil, Save, X, Star, Home,
  ShieldCheck, BadgeCheck, Mail, Lock, Percent, MessageSquare,
} from "lucide-react";

const eur = (n) => {
  const num = Number(n || 0);
  const [i, d] = Math.abs(num).toFixed(2).split(".");
  return `${num < 0 ? "-" : ""}${i.replace(/\B(?=(\d{3})+(?!\d))/g, ".")},${d} €`;
};
const firstImg = (p) => {
  const im = (p.images || [])[0];
  if (!im) return null;
  return im.storage_path ? fileUrl(im.storage_path) : im.url;
};
const dmy = (iso) => { try { return new Date(iso).toLocaleDateString("it-IT"); } catch { return "—"; } };

/* ---------------- Catalog + Cart ---------------- */
function CatalogTab({ articles, cart, setCart, shipConfig, salesConfig, onSubmit, submitting, profile, shipAddrId, setShipAddrId, locked }) {
  const { t } = useI18n();
  const addToCart = (article, variant) => {
    if (!variant) { toast.error(t("toast.select_size")); return; }
    const pr = article.pricing || {};
    const color = (article.colors || [])[0] || "";
    setCart((prev) => {
      const idx = prev.findIndex((i) => i.product_id === variant.product_id && i.size === variant.size);
      if (idx >= 0) {
        const copy = [...prev];
        copy[idx] = { ...copy[idx], quantity: copy[idx].quantity + 1 };
        return copy;
      }
      return [...prev, {
        product_id: variant.product_id, sku: variant.sku, name: article.name,
        size: variant.size || "", color, quantity: 1,
        unit_excl: pr.reseller_price_excl_vat ?? 0,
        list_excl: pr.price_excl_vat ?? 0,
        discount: pr.reseller_discount_pct ?? 0,
        vat_rate: pr.vat_rate ?? 22,
      }];
    });
    toast.success(t("toast.added", { item: `${article.name}${variant.size ? ` (${variant.size})` : ""}` }));
  };

  const updateQty = (i, delta) =>
    setCart((prev) => prev.map((it, idx) => idx === i ? { ...it, quantity: Math.max(1, it.quantity + delta) } : it));
  const removeItem = (i) => setCart((prev) => prev.filter((_, idx) => idx !== i));

  const grossExcl = cart.reduce((s, i) => s + (i.list_excl ?? i.unit_excl) * i.quantity, 0);
  const shipCountry = profile?.shipping_addresses?.find((a) => a.id === shipAddrId)?.country || "Italia";
  const vol = computeVolumeDiscount(shipCountry, grossExcl, salesConfig);
  const netExcl = vol.net;
  const ship = computeShipping(shipCountry, netExcl, shipConfig);
  const shipCost = ship.cost;
  const vatInfo = vatTreatment(shipCountry, profile?.vat_number);
  const vatBase = cart.reduce((s, i) => s + (i.list_excl ?? i.unit_excl) * i.quantity * vol.factor * (i.vat_rate / 100), 0);
  const vatTotal = vatInfo.exempt ? 0 : (vatBase + shipCost * (ship.vatRate / 100));
  const totalIncl = netExcl + shipCost + vatTotal;

  return (
    <div className="grid gap-6 lg:grid-cols-3">
      <div className="lg:col-span-2">
        {locked && (
          <div data-testid="b2b-locked-banner" className="mb-5 flex items-start gap-3 rounded-xl border border-brand/30 bg-brand/5 p-4">
            <Lock className="mt-0.5 h-5 w-5 shrink-0 text-brand" />
            <div className="text-sm">
              <p className="font-semibold text-foreground">{t("catalog.locked_title")}</p>
              <p className="mt-1 text-muted-foreground">{t("catalog.locked_body")}</p>
            </div>
          </div>
        )}
        {articles.length === 0 ? (
          <div className="rounded-xl border border-dashed border-border bg-brema-card/50 p-12 text-center text-muted-foreground">
            {t("catalog.empty")}
          </div>
        ) : (
          <div className="grid gap-5 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
            {articles.map((a, i) => (
              <ProductCard key={a.group_key} article={a} delay={i * 0.03} onAdd={addToCart} locked={locked} />
            ))}
          </div>
        )}
      </div>

      {/* Cart */}
      <div className="lg:sticky lg:top-24 lg:self-start">
        {locked ? (
          <div data-testid="cart-locked-panel" className="rounded-xl border border-border bg-brema-card p-5 text-center">
            <Lock className="mx-auto h-8 w-8 text-muted-foreground" />
            <h3 className="mt-3 font-semibold text-foreground">{t("cart.locked_title")}</h3>
            <p className="mt-1 text-sm text-muted-foreground">{t("cart.locked_body")}</p>
          </div>
        ) : (
        <div className="rounded-xl border border-border bg-brema-card p-5">
          <div className="mb-4 flex items-center gap-2 text-foreground">
            <ShoppingCart className="h-5 w-5 text-brand" />
            <h3 className="font-semibold">{t("cart.title")} <span className="text-xs font-normal text-muted-foreground">{t("cart.title_note")}</span></h3>
          </div>
          {cart.length === 0 ? (
            <p className="py-8 text-center text-sm text-muted-foreground">{t("cart.empty")}</p>
          ) : (
            <div className="space-y-3">
              {cart.map((it, i) => (
                <div key={`${it.product_id}-${it.size}-${it.color}`} data-testid="cart-item" className="rounded-lg bg-brema-bg p-2.5">
                  <div className="flex items-center gap-2">
                    <div className="flex-1">
                      <p className="text-sm font-medium text-foreground">{it.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {it.size && `${t("cart.size")} ${it.size}`}{it.color && ` · ${it.color}`} · {eur(it.list_excl ?? it.unit_excl)}
                      </p>
                    </div>
                    <div className="flex items-center gap-1">
                      <button onClick={() => updateQty(i, -1)} data-testid="cart-dec" className="rounded-md bg-muted p-1 text-foreground hover:bg-muted"><Minus className="h-3.5 w-3.5" /></button>
                      <span className="w-6 text-center text-sm text-foreground">{it.quantity}</span>
                      <button onClick={() => updateQty(i, 1)} data-testid="cart-inc" className="rounded-md bg-muted p-1 text-foreground hover:bg-muted"><Plus className="h-3.5 w-3.5" /></button>
                    </div>
                    <button onClick={() => removeItem(i)} data-testid="cart-remove" className="text-muted-foreground hover:text-brema-red"><Trash2 className="h-4 w-4" /></button>
                  </div>
                </div>
              ))}

              {profile?.shipping_addresses?.length > 0 && (
                <div className="space-y-1.5 pt-1">
                  <Label className="text-xs text-muted-foreground">{t("cart.ship_address")}</Label>
                  <Select value={shipAddrId} onValueChange={setShipAddrId}>
                    <SelectTrigger data-testid="ship-address-select" className="h-9 bg-brema-bg"><SelectValue placeholder={t("cart.choose_address")} /></SelectTrigger>
                    <SelectContent>
                      {profile.shipping_addresses.map((a) => (
                        <SelectItem key={a.id} value={a.id}>
                          {(a.label || a.city || "Indirizzo")}{a.city && a.label ? ` — ${a.city}` : ""}{a.is_default ? ` ${t("cart.default_suffix")}` : ""}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {shipAddrId && (
                    <p className="text-[11px] leading-snug text-muted-foreground">
                      {addrLine(profile.shipping_addresses.find((a) => a.id === shipAddrId))}
                    </p>
                  )}
                  <p className="text-[11px] text-muted-foreground">{t("cart.manage_addr_note")}</p>
                </div>
              )}

              <div className="space-y-1.5 border-t border-border pt-3 text-sm">
                {vol.amount > 0 && (
                  <>
                    <div className="flex items-center justify-between text-muted-foreground">
                      <span>{t("cart.list_subtotal")}</span><span data-testid="order-list-subtotal">{eur(grossExcl)}</span>
                    </div>
                    <div className="flex items-center justify-between font-medium text-positive" data-testid="order-volume-discount-row">
                      <span>{t("cart.volume_discount")} −{vol.effectivePct}%</span>
                      <span data-testid="order-volume-discount">−{eur(vol.amount)}</span>
                    </div>
                  </>
                )}
                <div className="flex items-center justify-between text-foreground">
                  <span>{t("cart.subtotal")}</span><span data-testid="order-subtotal">{eur(netExcl)}</span>
                </div>
                <div>
                  <div className="flex items-center justify-between text-foreground">
                    <span className="flex items-center gap-1"><Truck className="h-3.5 w-3.5 text-muted-foreground" /> {t("cart.shipping")}{ship.zoneLabel ? ` · ${ship.zoneLabel}` : ""}</span>
                    {ship.isFree
                      ? <span className="font-semibold text-green-600 dark:text-green-400" data-testid="order-shipping-free">{t("cart.free_shipping")}</span>
                      : <span data-testid="order-shipping">{eur(shipCost)}</span>}
                  </div>
                  {ship.threshold > 0 && !ship.isFree && (
                    <p className="mt-1 text-[11px] leading-snug text-muted-foreground" data-testid="shipping-note">
                      {t("cart.shipping_note_below", { threshold: eur(ship.threshold), zone: ship.zoneLabel, missing: eur(Math.max(0, ship.threshold - netExcl)) })}
                    </p>
                  )}
                  {ship.isFree && (
                    <p className="mt-1 text-[11px] leading-snug text-green-600 dark:text-green-400" data-testid="shipping-note">
                      {t("cart.shipping_note_free", { threshold: eur(ship.threshold), zone: ship.zoneLabel })}
                    </p>
                  )}
                </div>
                {vatInfo.exempt ? (
                  <div>
                    <div className="flex items-center justify-between text-foreground">
                      <span>{t("cart.vat")}</span><span className="text-xs italic text-muted-foreground" data-testid="order-vat-exempt">{t("cart.vat_exempt")}</span>
                    </div>
                    <p className="mt-1 text-[11px] leading-snug text-muted-foreground" data-testid="vat-exempt-note">{vatInfo.reason}</p>
                  </div>
                ) : (
                  <div className="flex items-center justify-between text-foreground">
                    <span>{t("cart.vat")}</span><span data-testid="order-vat">{eur(vatTotal)}</span>
                  </div>
                )}
                <div className="flex items-center justify-between border-t border-border pt-2 text-foreground">
                  <span className="font-semibold">{vatInfo.exempt ? t("cart.total_exempt") : t("cart.total_incl")}</span>
                  <span className="text-xl font-bold" data-testid="cart-total">{eur(totalIncl)}</span>
                </div>
              </div>
              <Button onClick={onSubmit} disabled={submitting} data-testid="submit-order-btn"
                className="w-full rounded-full bg-primary font-semibold text-primary-foreground hover:bg-primary/90">
                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : t("cart.submit")}
              </Button>
            </div>
          )}
        </div>
        )}
      </div>
    </div>
  );
}

function ProductCard({ article, onAdd, delay, locked }) {
  const { t } = useI18n();
  const variants = article.variants || [];
  const hasSizes = variants.some((v) => v.size);
  const vkey = (v) => `${v.product_id}::${v.size || "unica"}`;
  const [selKey, setSelKey] = useState(hasSizes ? "" : (variants[0] ? vkey(variants[0]) : ""));
  const [detailOpen, setDetailOpen] = useState(false);
  const pr = article.pricing || {};
  const img = firstImg(article);
  const selected = hasSizes ? variants.find((v) => vkey(v) === selKey) : variants[0];
  const stock = selected ? selected.stock : (article.total_stock ?? 0);
  const low = stock <= 10;

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: Math.min(delay, 0.3) }}
      data-testid="product-card"
      className="flex flex-col overflow-hidden rounded-xl border border-border bg-brema-card"
    >
      <button type="button" onClick={() => setDetailOpen(true)} data-testid="product-detail-trigger"
        className="group relative aspect-square overflow-hidden bg-brema-bg text-left">
        {img ? (
          <img src={img} alt={article.name} className="h-full w-full object-cover transition-transform duration-200 group-hover:scale-105" />
        ) : (
          <div className="flex h-full w-full items-center justify-center text-muted-foreground"><ImageIcon className="h-10 w-10" /></div>
        )}
        <span className="pointer-events-none absolute inset-0 flex items-center justify-center bg-black/0 text-transparent transition-colors group-hover:bg-black/25 group-hover:text-white">
          <Eye className="h-6 w-6" />
        </span>
      </button>
      <div className="flex flex-1 flex-col p-4">
        <button type="button" onClick={() => setDetailOpen(true)} data-testid="product-detail-name-trigger"
          className="text-left font-semibold text-foreground transition-colors hover:text-brand">
          {article.name}
        </button>
        <p className="text-xs text-muted-foreground">{article.line && <span className="font-bold uppercase tracking-wide text-foreground">{article.line}</span>}{article.model_code ? `${article.line ? " · " : ""}${article.model_code}` : ""}</p>
        {article.tech_specs && <p className="mt-1 line-clamp-2 text-xs text-muted-foreground">{article.tech_specs}</p>}

        {locked ? (
          <div className="mt-3 flex items-center gap-2 rounded-lg border border-dashed border-border bg-brema-bg p-3 text-xs text-muted-foreground" data-testid="product-locked-note">
            <Lock className="h-3.5 w-3.5 shrink-0 text-brand" />
            <span>{t("product.locked_note")}</span>
          </div>
        ) : (
        <>
        <div className="mt-3 space-y-1 rounded-lg bg-brema-bg p-3 text-xs">
          <div className="flex items-center justify-between text-muted-foreground">
            <span>{t("product.list_public")}</span>
            <span className="text-foreground">{eur(pr.price_incl_vat)} <span className="text-[10px] text-muted-foreground">{t("product.vat_incl")}</span></span>
          </div>
          <div className="flex items-center justify-between text-muted-foreground">
            <span>{t("product.list_excl")}</span><span>{eur(pr.price_excl_vat)}</span>
          </div>
        </div>

        <div className="mt-2 flex items-center justify-between">
          <span className={`text-xs font-medium ${low ? "text-brema-red" : "text-positive"}`}>
            {hasSizes && !selected ? t("product.pcs_total", { n: article.total_stock ?? 0 }) : (low ? t("product.only_pcs", { n: stock }) : t("product.pcs_avail", { n: stock }))}
          </span>
        </div>

        <div className="mt-3 space-y-2">
          {hasSizes && (
            <Select value={selKey} onValueChange={setSelKey}>
              <SelectTrigger className="h-9" data-testid="size-select"><SelectValue placeholder={t("product.size_ph")} /></SelectTrigger>
              <SelectContent>
                {variants.map((v) => (
                  <SelectItem key={vkey(v)} value={vkey(v)} disabled={v.stock <= 0}>
                    {v.size || t("product.size_unica")}{v.stock <= 0 ? ` — ${t("product.sold_out")}` : ` (${v.stock})`}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <Button size="sm" data-testid="add-to-cart-btn" onClick={() => onAdd(article, selected)}
            className="w-full rounded-full bg-primary font-semibold text-primary-foreground hover:bg-primary/90">
            <Plus className="mr-1 h-4 w-4" /> {t("product.add")}
          </Button>
        </div>
        </>
        )}
      </div>
      <ProductDetailDialog article={article} open={detailOpen} onOpenChange={setDetailOpen} locked={locked} onAdd={onAdd} />
    </motion.div>
  );
}

/* ---------------- Orders ---------------- */
function OrdersTab({ locked }) {
  const { t } = useI18n();
  const fmt = (d) => d.toISOString().slice(0, 10);
  const today = new Date();
  const yearAgo = new Date(); yearAgo.setFullYear(today.getFullYear() - 1);
  const [from, setFrom] = useState(fmt(yearAgo));
  const [to, setTo] = useState(fmt(today));
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);

  const load = (f = from, t = to) => {
    setLoading(true);
    api.get(`/orders?from_date=${f}&to_date=${t}`)
      .then(({ data }) => setOrders(data.orders))
      .catch(() => toast.error(t("toast.orders_load_error")))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const openDetail = async (id) => {
    try {
      const { data } = await api.get(`/orders/${id}`);
      setDetail(data.order); setDetailOpen(true);
    } catch { toast.error(t("toast.order_detail_error")); }
  };

  return (
    <div className="space-y-4">
      {locked && (
        <div data-testid="orders-locked-banner" className="flex items-start gap-3 rounded-xl border border-brand/30 bg-brand/5 p-4">
          <Lock className="mt-0.5 h-5 w-5 shrink-0 text-brand" />
          <div className="text-sm">
            <p className="font-semibold text-foreground">{t("orders.locked_title")}</p>
            <p className="mt-1 text-muted-foreground">{t("orders.locked_body")}</p>
          </div>
        </div>
      )}
      <div className="flex flex-wrap items-end gap-3 rounded-xl border border-border bg-brema-card p-4">
        <div className="flex items-center gap-2 text-foreground"><CalendarDays className="h-4 w-4 text-brand" /><span className="text-sm font-medium">{t("orders.period")}</span></div>
        <div className="space-y-1"><Label className="text-[11px] text-muted-foreground">{t("orders.from")}</Label>
          <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} data-testid="orders-from-date" className="h-9 w-[160px] bg-brema-bg" /></div>
        <div className="space-y-1"><Label className="text-[11px] text-muted-foreground">{t("orders.to")}</Label>
          <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} data-testid="orders-to-date" className="h-9 w-[160px] bg-brema-bg" /></div>
        <Button onClick={() => load()} disabled={loading} data-testid="orders-apply-filter"
          className="h-9 rounded-full bg-primary font-semibold text-primary-foreground hover:bg-primary/90">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : t("orders.apply")}
        </Button>
        <Button variant="secondary" onClick={() => { setFrom(fmt(yearAgo)); setTo(fmt(today)); load(fmt(yearAgo), fmt(today)); }}
          data-testid="orders-reset-filter" className="h-9 rounded-full border border-border bg-transparent text-xs text-foreground hover:bg-brema-hover">
          {t("orders.last12")}
        </Button>
        <span className="ml-auto text-xs text-muted-foreground">{t("orders.count", { n: orders.length })}</span>
      </div>

      {orders.length === 0 ? (
        <div className="rounded-xl border border-dashed border-border bg-brema-card/50 p-12 text-center text-muted-foreground">
          {t("orders.empty")}
        </div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-border bg-brema-card">
          <table className="w-full min-w-[720px] text-sm">
            <thead className="border-b border-border bg-brema-bg text-left text-[11px] uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="p-3.5">{t("orders.col_date")}</th>
                <th className="p-3.5">{t("orders.col_id")}</th>
                <th className="p-3.5">{t("orders.col_recipient")}</th>
                <th className="p-3.5">{t("orders.col_status")}</th>
                <th className="p-3.5 text-right">{t("orders.col_total")}</th>
                <th className="p-3.5 text-right"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {orders.map((o) => (
                <tr key={o._id} data-testid="order-row" onClick={() => openDetail(o._id)}
                  className="cursor-pointer transition-colors hover:bg-brema-hover">
                  <td className="p-3.5 text-foreground">{dmy(o.created_at)}</td>
                  <td className="p-3.5 font-mono text-xs text-muted-foreground">#{o._id.slice(-6).toUpperCase()}</td>
                  <td className="p-3.5">
                    <p className="font-medium text-foreground">{o.recipient || o.reseller_name || "—"}</p>
                    <p className="text-xs text-muted-foreground">{o.ship_city || (o.shipping_address?.city) || ""}</p>
                  </td>
                  <td className="p-3.5"><OrderStatusBadge status={o.status} /></td>
                  <td className="p-3.5 text-right font-semibold text-foreground">{eur(o.total_incl_vat ?? o.total)}</td>
                  <td className="p-3.5 text-right">
                    <Button size="sm" variant="secondary" data-testid="view-order-btn"
                      onClick={(e) => { e.stopPropagation(); openDetail(o._id); }}
                      className="rounded-full border border-border bg-transparent text-xs text-foreground hover:bg-brema-hover">
                      <Eye className="mr-1.5 h-3.5 w-3.5" /> {t("orders.detail")}
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <OrderDetailDialog order={detail} open={detailOpen} onOpenChange={setDetailOpen} />
    </div>
  );
}

/* ---------------- Marketing Hub ---------------- */
function AssetThumb({ a }) {
  const src = a.file_path ? fileUrl(a.file_path) : a.url;
  const t = (a.type || "").toUpperCase();
  const isVid = ["MP4", "MOV", "WEBM", "VIDEO", "M4V"].includes(t);
  if (a.is_image && src) {
    return <img src={src} alt={a.title} className="h-full w-full object-cover" />;
  }
  if (isVid && src) {
    return (
      <video src={`${src}#t=0.1`} className="h-full w-full bg-black object-contain" preload="metadata"
        muted playsInline controls data-testid="asset-video-preview" />
    );
  }
  if (a.thumb_path) {
    return <img src={fileUrl(a.thumb_path)} alt={a.title} className="h-full w-full object-cover object-top" data-testid="pdf-cover-thumb" />;
  }
  const Icon = (t === "PDF" || t === "TESTO") ? FileText : (isVid ? Film : FileArchive);
  return (
    <div className="flex h-full w-full flex-col items-center justify-center gap-2 bg-brema-bg text-muted-foreground">
      <Icon className="h-9 w-9" />
      <span className="text-[11px] font-semibold uppercase tracking-wider">{t || "FILE"}</span>
    </div>
  );
}

const MARKETING_CATEGORIES = ["Catalogo", "Video", "Foto", "Social", "Materiale Punto vendita", "Varie"];

function MarketingTab({ assets, articles }) {
  const { t } = useI18n();
  const [catFilter, setCatFilter] = useState("Tutte");
  const shownAssets = catFilter === "Tutte" ? (assets || []) : (assets || []).filter((a) => (a.category || "") === catFilter);
  const [topic, setTopic] = useState("");
  const [productName, setProductName] = useState("nessuno");
  const [tone, setTone] = useState("energico");
  const [platform, setPlatform] = useState("instagram");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);
  const productNames = Array.from(new Set((articles || []).map((a) => a.name).filter(Boolean)));

  const generate = async () => {
    if (!topic.trim()) { toast.error(t("toast.enter_topic")); return; }
    setLoading(true); setResult(null);
    try {
      const { data } = await api.post("/marketing/social-copy", {
        topic, tone, platform,
        product_name: productName === "nessuno" ? null : productName,
      });
      setResult(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  };

  const fullText = result ? `${result.caption}\n\n${(Array.isArray(result.hashtags) ? result.hashtags : []).map((h) => "#" + h).join(" ")}\n\n${result.cta}` : "";
  const [copiedAssetId, setCopiedAssetId] = useState(null);
  const copyAssetText = (a) => {
    navigator.clipboard.writeText(a.body_text || "");
    setCopiedAssetId(a._id);
    setTimeout(() => setCopiedAssetId(null), 1500);
  };

  return (
    <div className="grid gap-6 lg:grid-cols-2">
      {/* Assets */}
      <div>
        <div className="mb-4 flex flex-wrap items-center gap-2 text-foreground">
          <Images className="h-5 w-5 text-brand" />
          <h3 className="font-semibold">{t("mk.assets_title")}</h3>
          <Select value={catFilter} onValueChange={setCatFilter}>
            <SelectTrigger className="ml-auto h-8 w-[190px] bg-brema-bg text-xs" data-testid="marketing-category-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="Tutte">{t("mk.all_categories")}</SelectItem>
              {MARKETING_CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        {shownAssets.length === 0 ? (
          <p className="rounded-xl border border-dashed border-border bg-brema-card/50 p-8 text-center text-sm text-muted-foreground">{t("mk.empty_category")}</p>
        ) : (
        <div className="grid gap-4 sm:grid-cols-2">
          {shownAssets.map((a) => {
            const dl = a.file_path ? fileUrl(a.file_path) : a.url;
            return (
            <div key={a._id} data-testid="asset-card" className="flex flex-col overflow-hidden rounded-xl border border-border bg-brema-card">
              <div className="aspect-video overflow-hidden bg-brema-bg">
                <AssetThumb a={a} />
              </div>
              <div className="flex flex-1 flex-col p-4">
                <div className="mb-2 flex flex-wrap items-center gap-2">
                  <span className="rounded-full bg-brema-bg px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-brand">{a.type}</span>
                  {a.platform && <span className="rounded-full bg-muted px-2 py-0.5 text-[10px] font-medium text-foreground">{a.platform}</span>}
                  <span className="text-xs text-muted-foreground">{a.size}</span>
                  {a.category && <span className="text-xs text-muted-foreground">· {a.category}</span>}
                </div>
                <p className="mb-2 text-sm font-medium text-foreground">{a.title}</p>
                {a.body_text && (
                  <div className="mb-3">
                    <p className="max-h-24 overflow-y-auto whitespace-pre-line rounded-md bg-brema-bg p-2 text-xs text-foreground">{a.body_text}</p>
                    <Button type="button" size="sm" variant="secondary" data-testid="copy-asset-text-btn"
                      onClick={() => copyAssetText(a)}
                      className="mt-2 w-full rounded-full border border-border bg-transparent text-xs hover:bg-brema-hover">
                      {copiedAssetId === a._id ? <Check className="mr-1 h-3.5 w-3.5" /> : <Copy className="mr-1 h-3.5 w-3.5" />}
                      {copiedAssetId === a._id ? t("mk.copied") : t("mk.copy_text")}
                    </Button>
                  </div>
                )}
                {dl && (
                  <Button asChild size="sm" data-testid="download-asset-btn"
                    className="mt-auto w-full rounded-full border border-border bg-transparent text-foreground hover:bg-brema-hover">
                    <a href={dl} target="_blank" rel="noreferrer" download={a.original_filename || a.title}
                      onClick={() => api.post("/portal/event", { type: "download", item: { kind: "asset", id: a._id, title: a.title, filename: a.original_filename || "", category: a.category || "" } }).catch(() => {})}>
                      <Download className="mr-1.5 h-3.5 w-3.5" /> {t("mk.download")}</a>
                  </Button>
                )}
              </div>
            </div>
          );})}
        </div>
        )}
      </div>

      {/* AI social copy */}
      <div>
        <div className="mb-4 flex items-center gap-2 text-foreground">
          <Sparkles className="h-5 w-5 text-brand" />
          <h3 className="font-semibold">{t("mk.ai_title")}</h3>
        </div>
        <div className="rounded-xl border border-border bg-brema-card p-5">
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>{t("mk.topic")}</Label>
              <Input value={topic} onChange={(e) => setTopic(e.target.value)} data-testid="copy-topic"
                placeholder={t("mk.topic_ph")} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>{t("mk.product_opt")}</Label>
                <Select value={productName} onValueChange={setProductName}>
                  <SelectTrigger data-testid="copy-product"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="nessuno">{t("mk.none")}</SelectItem>
                    {productNames.map((n) => <SelectItem key={n} value={n}>{n}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>{t("mk.platform")}</Label>
                <Select value={platform} onValueChange={setPlatform}>
                  <SelectTrigger data-testid="copy-platform"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="instagram">Instagram</SelectItem>
                    <SelectItem value="facebook">Facebook</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>{t("mk.tone")}</Label>
              <Select value={tone} onValueChange={setTone}>
                <SelectTrigger data-testid="copy-tone"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="energico">{t("mk.tone_energico")}</SelectItem>
                  <SelectItem value="elegante">{t("mk.tone_elegante")}</SelectItem>
                  <SelectItem value="tecnico">{t("mk.tone_tecnico")}</SelectItem>
                  <SelectItem value="amichevole">{t("mk.tone_amichevole")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button onClick={generate} disabled={loading} data-testid="generate-copy-btn"
              className="w-full rounded-full bg-primary font-semibold text-primary-foreground hover:bg-primary/90">
              {loading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> {t("mk.generating")}</> : <><Sparkles className="mr-2 h-4 w-4" /> {t("mk.generate")}</>}
            </Button>

            {result && (
              <div className="rounded-lg bg-brema-bg p-4" data-testid="copy-result">
                <p className="whitespace-pre-line text-sm text-foreground">{result.caption}</p>
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {(Array.isArray(result.hashtags) ? result.hashtags : []).map((h, i) => <span key={i} className="text-xs text-brand">#{h}</span>)}
                </div>
                {result.cta && <p className="mt-3 text-sm font-medium text-foreground">{result.cta}</p>}
                <Button type="button" variant="secondary" size="sm" data-testid="copy-result-btn"
                  onClick={() => { navigator.clipboard.writeText(fullText); setCopied(true); setTimeout(() => setCopied(false), 1500); }}
                  className="mt-4 rounded-full border border-border bg-transparent text-xs hover:bg-brema-hover">
                  {copied ? <Check className="mr-1 h-3.5 w-3.5" /> : <Copy className="mr-1 h-3.5 w-3.5" />}
                  {copied ? t("mk.copied") : t("mk.copy_text")}
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ---------------- Reseller Profile ---------------- */
function ProfileTab({ profile, reloadProfile, tier }) {
  const { t } = useI18n();
  const [phone, setPhone] = useState(profile?.phone || "");
  const [contactEmail, setContactEmail] = useState(profile?.contact_email || "");
  const [addrs, setAddrs] = useState(profile?.shipping_addresses || []);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(null); // index or "new"
  const [draft, setDraft] = useState(emptyAddr);
  const [dlgOpen, setDlgOpen] = useState(false);
  const [contactOpen, setContactOpen] = useState(false);

  useEffect(() => {
    setPhone(profile?.phone || "");
    setContactEmail(profile?.contact_email || "");
    setAddrs(profile?.shipping_addresses || []);
  }, [profile]);

  const persist = async (nextAddrs = addrs, extra = {}) => {
    setSaving(true);
    try {
      await api.patch("/profile", {
        phone, contact_email: contactEmail, shipping_addresses: nextAddrs, ...extra,
      });
      toast.success(t("toast.profile_saved"));
      reloadProfile();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || t("toast.save_failed"));
    } finally { setSaving(false); }
  };

  const openNewAddr = () => { setEditing("new"); setDraft({ ...emptyAddr }); setDlgOpen(true); };
  const openEditAddr = (i) => { setEditing(i); setDraft({ ...addrs[i] }); setDlgOpen(true); };
  const saveAddr = async () => {
    if (!draft.recipient?.trim() && !draft.city?.trim()) { toast.error(t("toast.addr_min")); return; }
    let next;
    if (editing === "new") next = [...addrs, { ...draft }];
    else next = addrs.map((a, i) => (i === editing ? { ...draft, id: a.id } : a));
    setAddrs(next); setDlgOpen(false);
    await persist(next);
  };
  const delAddr = async (i) => {
    if (!window.confirm(t("toast.del_addr_confirm"))) return;
    const next = addrs.filter((_, idx) => idx !== i);
    setAddrs(next); await persist(next);
  };
  const makeDefault = async (i) => {
    const next = addrs.map((a, idx) => ({ ...a, is_default: idx === i }));
    setAddrs(next); await persist(next);
  };

  const billing = profile?.billing_address || {};
  return (
    <div className="grid gap-6 lg:grid-cols-2">
      {/* Fiscal (locked) */}
      <div className="rounded-xl border border-border bg-brema-card p-5">
        <div className="mb-4 flex items-center gap-2 text-foreground"><Building2 className="h-5 w-5 text-brand" /><h3 className="font-semibold">{t("pf.fiscal_title")}</h3></div>
        <p className="mb-4 text-xs text-muted-foreground">{t("pf.fiscal_note")}</p>
        <dl className="space-y-3 text-sm" data-testid="fiscal-data">
          <div><dt className="text-xs uppercase tracking-wider text-muted-foreground">{t("pf.company")}</dt><dd className="text-foreground">{profile?.company_name || profile?.shop_name || "—"}</dd></div>
          <div><dt className="text-xs uppercase tracking-wider text-muted-foreground">{t("pf.vat")}</dt><dd className="text-foreground">{profile?.vat_number || "—"}</dd></div>
          <div><dt className="text-xs uppercase tracking-wider text-muted-foreground">{t("pf.billing")}</dt>
            <dd className="text-foreground">{billing.recipient ? <>{billing.recipient}<br />{addrLine(billing)}</> : "—"}</dd></div>
          <div><dt className="text-xs uppercase tracking-wider text-muted-foreground">{t("pf.account_email")}</dt><dd className="text-foreground">{profile?.email}</dd></div>
        </dl>
        {tier !== "ufficiale" && (
          <div className="mt-4 rounded-lg border border-brand/25 bg-brand/5 p-3" data-testid="above59-note">
            <p className="text-xs leading-relaxed text-muted-foreground">{t("pf.above59_note")}</p>
            <Button data-testid="contact-sales-btn" onClick={() => setContactOpen(true)}
              className="mt-3 w-full rounded-full bg-brema-red font-semibold text-white hover:bg-brema-red/90">
              <Mail className="mr-2 h-4 w-4" /> {t("pf.contact_us")}
            </Button>
          </div>
        )}
      </div>

      {/* Modale Contattaci (condivisa con il pulsante «Scrivi all'azienda» in testata) */}
      <ContactCompanyDialog open={contactOpen} onOpenChange={setContactOpen} />

      {/* Editable contacts */}
      <div className="rounded-xl border border-border bg-brema-card p-5">
        <div className="mb-4 flex items-center gap-2 text-foreground"><User className="h-5 w-5 text-brand" /><h3 className="font-semibold">{t("pf.contacts")}</h3></div>
        <div className="space-y-3">
          <div className="space-y-1.5"><Label className="text-xs">{t("pf.phone")}</Label>
            <Input value={phone} onChange={(e) => setPhone(e.target.value)} data-testid="profile-phone" className="bg-brema-bg" /></div>
          <div className="space-y-1.5"><Label className="text-xs">{t("pf.contact_email")}</Label>
            <Input value={contactEmail} onChange={(e) => setContactEmail(e.target.value)} data-testid="profile-contact-email" className="bg-brema-bg" /></div>
          <Button onClick={() => persist()} disabled={saving} data-testid="save-profile-btn"
            className="rounded-full bg-primary font-semibold text-primary-foreground hover:bg-primary/90">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="mr-2 h-4 w-4" /> {t("pf.save_contacts")}</>}
          </Button>
        </div>
      </div>

      {/* Shipping addresses */}
      <div className="rounded-xl border border-border bg-brema-card p-5 lg:col-span-2">
        <div className="mb-4 flex items-center justify-between">
          <div className="flex items-center gap-2 text-foreground"><Truck className="h-5 w-5 text-brand" /><h3 className="font-semibold">{t("pf.ship_addresses")}</h3></div>
          <Button size="sm" onClick={openNewAddr} data-testid="add-address-btn"
            className="rounded-full bg-primary font-semibold text-primary-foreground hover:bg-primary/90"><Plus className="mr-1.5 h-3.5 w-3.5" /> {t("pf.new_address")}</Button>
        </div>
        {addrs.length === 0 ? (
          <p className="py-6 text-center text-sm text-muted-foreground">{t("pf.no_addresses")}</p>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2">
            {addrs.map((a, i) => (
              <div key={a.id || i} data-testid="address-card" className="rounded-lg border border-border bg-brema-bg p-4">
                <div className="mb-1.5 flex items-center gap-2">
                  <Home className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm font-medium text-foreground">{a.label || a.city || "Indirizzo"}</span>
                  {a.is_default && <span className="rounded-full bg-primary/20 px-2 py-0.5 text-[10px] font-semibold text-brand">{t("pf.default")}</span>}
                </div>
                <p className="text-sm text-foreground">{a.recipient}</p>
                <p className="text-xs text-muted-foreground">{addrLine(a)}</p>
                <div className="mt-3 flex flex-wrap gap-2">
                  <Button size="sm" variant="secondary" onClick={() => openEditAddr(i)} data-testid="edit-address-btn"
                    className="rounded-full border border-border bg-transparent text-xs text-foreground hover:bg-brema-hover"><Pencil className="mr-1 h-3 w-3" /> {t("common.edit")}</Button>
                  {!a.is_default && <Button size="sm" variant="secondary" onClick={() => makeDefault(i)} data-testid="set-default-address-btn"
                    className="rounded-full border border-border bg-transparent text-xs text-foreground hover:bg-brema-hover"><Star className="mr-1 h-3 w-3" /> {t("pf.set_default")}</Button>}
                  <Button size="sm" variant="danger" onClick={() => delAddr(i)} data-testid="delete-address-btn"
                    className="rounded-full text-xs"><Trash2 className="h-3 w-3" /></Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <Dialog open={dlgOpen} onOpenChange={setDlgOpen}>
        <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto border-border bg-brema-card">
          <DialogHeader>
            <DialogTitle className="text-foreground">{editing === "new" ? t("pf.new_address") : t("pf.edit_address")}</DialogTitle>
            <DialogDescription>{t("pf.address_desc")}</DialogDescription>
          </DialogHeader>
          <AddressFields value={draft} onChange={setDraft} />
          <DialogFooter>
            <Button onClick={saveAddr} disabled={saving} data-testid="save-address-btn"
              className="rounded-full bg-primary font-semibold text-primary-foreground hover:bg-primary/90">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : t("pf.save_address")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ---------------- Portal shell ---------------- */
function ShippingRulesView({ config }) {
  const { t } = useI18n();
  if (!config) {
    return <div className="py-10 text-center text-muted-foreground"><Loader2 className="mx-auto h-5 w-5 animate-spin" /></div>;
  }
  return (
    <div className="max-w-3xl space-y-4" data-testid="portal-shipping-view">
      <div className="rounded-xl border border-border bg-brema-card p-5">
        <div className="mb-1 flex items-center gap-2 text-foreground">
          <Truck className="h-5 w-5 text-brema-red dark:text-brand" />
          <h3 className="font-semibold">{t("ship.rules_title")}</h3>
          <span className="ml-auto inline-flex items-center gap-1 rounded-full border border-border bg-brema-bg px-2 py-0.5 text-[11px] text-muted-foreground">
            <Lock className="h-3 w-3" /> {t("readonly.badge")}
          </span>
        </div>
        <p className="mb-4 text-sm text-muted-foreground">{t("ship.readonly_intro")}</p>
        <div className="space-y-3">
          {(config.zones || []).map((z) => (
            <div key={z.key} className="rounded-lg border border-border bg-brema-bg p-4" data-testid={`portal-ship-zone-${z.key}`}>
              <p className="mb-3 font-medium text-foreground">{z.label}</p>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div>
                  <p className="text-xs text-muted-foreground">{t("ship.free_threshold")}</p>
                  <p className="font-semibold text-foreground" data-testid={`portal-ship-threshold-${z.key}`}>{eur(z.free_threshold)}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">{t("ship.flat_cost")}</p>
                  <p className="font-semibold text-foreground" data-testid={`portal-ship-cost-${z.key}`}>{eur(z.flat_cost)}</p>
                </div>
              </div>
              <p className="mt-3 text-xs text-muted-foreground">
                {t("ship.note", { threshold: eur(z.free_threshold), cost: eur(z.flat_cost), free: t("ship.free") })}
              </p>
            </div>
          ))}
        </div>
        <div className="mt-4 border-t border-border pt-3">
          <p className="text-xs text-muted-foreground">{t("ship.vat_transport")}</p>
          <p className="font-semibold text-foreground" data-testid="portal-ship-vat">{Number(config.vat_rate ?? 22)}%</p>
        </div>
      </div>
    </div>
  );
}

function SalesConditionsView({ config }) {
  const { t } = useI18n();
  if (!config) {
    return <div className="py-10 text-center text-muted-foreground"><Loader2 className="mx-auto h-5 w-5 animate-spin" /></div>;
  }
  return (
    <div className="max-w-3xl space-y-4" data-testid="portal-sales-view">
      <div className="rounded-xl border border-border bg-brema-card p-5">
        <div className="mb-1 flex items-center gap-2 text-foreground">
          <Percent className="h-5 w-5 text-brema-red dark:text-brand" />
          <h3 className="font-semibold">{t("sc.title")}</h3>
          <span className="ml-auto inline-flex items-center gap-1 rounded-full border border-border bg-brema-bg px-2 py-0.5 text-[11px] text-muted-foreground">
            <Lock className="h-3 w-3" /> {t("readonly.badge")}
          </span>
        </div>
        <p className="mb-4 text-sm text-muted-foreground">{t("sc.readonly_intro")}</p>
        <div className="space-y-4">
          {(config.zones || []).map((z) => (
            <div key={z.key} className="rounded-lg border border-border bg-brema-bg p-4" data-testid={`portal-sales-zone-${z.key}`}>
              <p className="mb-3 font-medium text-foreground">{z.label}</p>
              <div className="overflow-hidden rounded-lg border border-border">
                <table className="w-full text-sm">
                  <thead className="bg-brema-card text-[11px] uppercase tracking-wide text-muted-foreground">
                    <tr>
                      <th className="p-2.5 text-left">{t("sc.col_range")}</th>
                      <th className="p-2.5 text-right">{t("sc.col_discount")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(z.tiers || []).map((tr, i) => {
                      const parts = parseDiscountExpr(tr.discount);
                      const eff = effectivePct(parts);
                      const label = parts.map((d) => `${d}%`).join(" + ") || "0%";
                      const range = (tr.max === null || tr.max === undefined)
                        ? t("sc.over", { from: eur(tr.min) })
                        : `${eur(tr.min)} – ${eur(tr.max)}`;
                      return (
                        <tr key={i} className="border-t border-border" data-testid={`portal-sales-tier-${z.key}-${i}`}>
                          <td className="p-2.5 text-foreground">{range}</td>
                          <td className="p-2.5 text-right font-semibold text-brema-red dark:text-brand" data-testid={`portal-sales-eff-${z.key}-${i}`}>
                            {label}{parts.length > 1 ? <span className="ml-1 text-[11px] font-normal text-muted-foreground">= {eff}%</span> : null}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function Portal() {
  const { user } = useAuth();
  const { t } = useI18n();
  const [articles, setArticles] = useState([]);
  const [assets, setAssets] = useState([]);
  const [profile, setProfile] = useState(null);
  const [cart, setCart] = useState([]);
  const [cartLoaded, setCartLoaded] = useState(false);
  const [shipConfig, setShipConfig] = useState(null);
  const [salesConfig, setSalesConfig] = useState(null);
  const [shipAddrId, setShipAddrId] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [note, setNote] = useState("");
  const [lockedB2B, setLockedB2B] = useState(false);
  const [tier, setTier] = useState("");
  const [contactOpen, setContactOpen] = useState(false);
  const [sp] = useSearchParams();
  const initialTab = sp.get("tab") || "catalog";

  const loadCatalog = () => api.get("/catalog").then(({ data }) => {
    setArticles(data.articles);
    setLockedB2B(!!data.locked_b2b);
    setTier(data.reseller_tier || "");
  }).catch(() => {});
  const loadProfile = () => api.get("/profile").then(({ data }) => {
    setProfile(data.profile);
    const list = data.profile?.shipping_addresses || [];
    const def = list.find((a) => a.is_default) || list[0];
    if (def) setShipAddrId((prev) => prev || def.id);
  }).catch(() => {});

  useEffect(() => {
    loadCatalog();
    api.get("/marketing-assets").then(({ data }) => setAssets(data.assets)).catch(() => {});
    api.get("/shipping-config").then(({ data }) => setShipConfig(data.config)).catch(() => {});
    api.get("/sales-conditions").then(({ data }) => setSalesConfig(data.config)).catch(() => {});
    api.get("/cart").then(({ data }) => setCart(data.cart?.items || [])).catch(() => {}).finally(() => setCartLoaded(true));
    loadProfile();
  }, []);

  // Persist cart server-side (debounced) so Admin can see pending/abandoned carts
  useEffect(() => {
    if (!cartLoaded) return;
    const t = setTimeout(() => { api.put("/cart", { items: cart }).catch(() => {}); }, 800);
    return () => clearTimeout(t);
  }, [cart, cartLoaded]);

  const submitOrder = async () => {
    if (cart.length === 0) return;
    setSubmitting(true);
    try {
      const items = cart.map((it) => ({
        product_id: it.product_id, sku: it.sku, name: it.name,
        size: it.size, color: it.color, quantity: it.quantity,
      }));
      const res = await api.post("/orders", { items, note, shipping_address_id: shipAddrId || null });
      const orderId = res.data.order._id;
      toast.success(t("toast.order_sent"));
      setCart([]); setNote(""); loadCatalog();
      // Best-effort: email the PDF order confirmation (reseller + Brema in copia)
      try {
        const { data } = await api.get(`/orders/${orderId}`);
        const b64 = await buildOrderPdfBase64(data.order);
        const er = await api.post(`/orders/${orderId}/email-confirmation`, {
          pdf_base64: b64, filename: `Ordine_${orderId.slice(-6).toUpperCase()}.pdf`,
        });
        if (er.data?.sent) toast.success(t("toast.order_confirm_email"));
      } catch (e) { /* email best-effort: non blocca l'ordine */ }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setSubmitting(false); }
  };

  return (
    <div>
      <PageHeader
        eyebrow={t("portal.eyebrow")}
        title={user?.shop_name || t("portal.title_fallback")}
        subtitle={t("portal.subtitle")}
      />
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        {tier === "ufficiale" ? (
          <span data-testid="tier-badge" className="inline-flex items-center gap-1.5 rounded-full border border-brema-red/40 bg-brema-red/10 px-3 py-1 text-xs font-semibold text-brema-red dark:border-brand/40 dark:bg-brand/10 dark:text-brand">
            <ShieldCheck className="h-3.5 w-3.5" /> {t("tier.official")}
          </span>
        ) : (
          <span data-testid="tier-badge" className="inline-flex items-center gap-1.5 rounded-full border border-positive/40 bg-positive/10 px-3 py-1 text-xs font-semibold text-positive">
            <BadgeCheck className="h-3.5 w-3.5" /> {t("tier.potential")}
          </span>
        )}
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" data-testid="portal-contact-company-btn" onClick={() => setContactOpen(true)}
            className="rounded-full border-border bg-brema-card text-xs font-semibold text-foreground hover:bg-brema-hover">
            <MessageSquare className="mr-1.5 h-3.5 w-3.5 text-brand" /> {t("pf.write_company")}
          </Button>
          <LangSwitcher />
        </div>
      </div>
      <ContactCompanyDialog open={contactOpen} onOpenChange={setContactOpen} />
      <ResellerNotifications enabled={tier === "ufficiale"} />
      <Tabs defaultValue={initialTab}>
        <TabsList className="flex-wrap bg-brema-card">
          <TabsTrigger value="catalog" data-testid="tab-catalog"><Package className="mr-1.5 h-4 w-4" /> {t("tab.catalog")}</TabsTrigger>
          <TabsTrigger value="orders" data-testid="tab-orders"><ShoppingCart className="mr-1.5 h-4 w-4" /> {t("tab.orders")}</TabsTrigger>
          <TabsTrigger value="marketing" data-testid="tab-marketing"><Images className="mr-1.5 h-4 w-4" /> {t("tab.marketing")}</TabsTrigger>
          <TabsTrigger value="profile" data-testid="tab-profile"><User className="mr-1.5 h-4 w-4" /> {t("tab.profile")}</TabsTrigger>
          {tier === "ufficiale" && (
            <>
              <TabsTrigger value="shipping" data-testid="tab-portal-shipping"><Truck className="mr-1.5 h-4 w-4" /> {t("tab.shipping")}</TabsTrigger>
              <TabsTrigger value="sales-conditions" data-testid="tab-portal-sales"><Percent className="mr-1.5 h-4 w-4" /> {t("tab.sales_conditions")}</TabsTrigger>
            </>
          )}
        </TabsList>

        <TabsContent value="catalog">
          <CatalogTab articles={articles} cart={cart} setCart={setCart} locked={lockedB2B}
            shipConfig={shipConfig} salesConfig={salesConfig} onSubmit={submitOrder} submitting={submitting}
            profile={profile} shipAddrId={shipAddrId} setShipAddrId={setShipAddrId} />
        </TabsContent>
        <TabsContent value="orders">
          <OrdersTab locked={lockedB2B} />
        </TabsContent>
        <TabsContent value="marketing">
          <MarketingTab assets={assets} articles={articles} />
        </TabsContent>
        <TabsContent value="profile">
          <ProfileTab profile={profile} reloadProfile={loadProfile} tier={tier} />
        </TabsContent>
        {tier === "ufficiale" && (
          <>
            <TabsContent value="shipping"><ShippingRulesView config={shipConfig} /></TabsContent>
            <TabsContent value="sales-conditions"><SalesConditionsView config={salesConfig} /></TabsContent>
          </>
        )}
      </Tabs>
    </div>
  );
}
